import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Loader2, CheckCircle2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHOTO_COUNTS = ["Under 20", "20–40", "40–60", "More than 60", "Not sure yet"];
const MUSIC_PREFS = [
  "A specific song (I'll share it below)",
  "Gentle piano",
  "Classical / orchestral",
  "Uplifting & hopeful",
  "Let your team choose",
];
const STYLES = [
  { value: "celebration", label: "Celebration (In Color)" },
  { value: "legacy", label: "Legacy (Black & White)" },
  { value: "blend", label: "A blend of both" },
];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    loved_one_name: "",
    service_date: "",
    photo_count: "",
    music_preference: "",
    style: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.loved_one_name) {
      toast.error("Please fill in your name, email, and loved one's name.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/tribute-requests`, form);
      setDone(data);
      toast.success("Your request has been received.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or email us directly.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Begin</div>
            <h2 className="serif text-4xl sm:text-5xl font-light leading-[1.05] text-[#F5F0E8]">
              Start your
              <span className="italic text-[#E6B89C]"> tribute.</span>
            </h2>
            <p className="mt-5 text-[#A0988E] spectral leading-relaxed">
              Tell us a little about your loved one. As soon as you submit, you&apos;ll get a
              private link to upload their photos. You pay by Cash App once your photos are in.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(212,175,55,0.3)] bg-[rgba(20,19,17,0.5)]">
                  <Mail size={16} className="text-[#D4AF37]" />
                </span>
                <div>
                  <div className="text-xs tracking-widest uppercase text-[#C5A55A]/80">Prefer email?</div>
                  <a
                    data-testid="email-link"
                    href="mailto:eternallegacytributes@gmail.com?subject=Tribute%20Video%20Request"
                    className="serif text-[18px] sm:text-[20px] text-[#F5F0E8] hover:text-[#E6B89C] transition-colors break-all"
                  >
                    eternallegacytributes@gmail.com
                  </a>
                </div>
              </div>

              <div className="mt-8 card-surface p-5 text-sm text-[#A0988E] spectral italic leading-relaxed">
                "We're a small team of editors, and we only accept a limited number of
                tributes each week — so each family receives our full attention."
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div data-testid="contact-success" className="card-surface p-10 text-center">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.08)] mb-5">
                  <CheckCircle2 size={26} className="text-[#D4AF37]" strokeWidth={1.4} />
                </span>
                <h3 className="serif text-3xl text-[#F5F0E8] font-light">Thank you — your request is received.</h3>
                <p className="mt-4 text-[#A0988E] spectral max-w-md mx-auto leading-relaxed">
                  Your private photo upload link is ready. We&apos;ve also emailed it to <span className="text-[#F5F0E8]">{form.email}</span> so
                  you can come back and add photos any time.
                </p>
                <a href={`/upload/${done.upload_token}`} data-testid="go-to-upload-button" className="btn-primary mt-8">
                  <Upload size={16} /> Upload Photos Now
                </a>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={onSubmit} className="card-surface p-6 sm:p-8 lg:p-10 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="field-label">Your name *</label>
                    <input data-testid="input-name" value={form.name} onChange={update("name")} placeholder="Jane Doe" required />
                  </div>
                  <div>
                    <label className="field-label">Email *</label>
                    <input data-testid="input-email" type="email" value={form.email} onChange={update("email")} placeholder="jane@example.com" required />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="field-label">Phone (optional)</label>
                    <input data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <label className="field-label">Loved one's name *</label>
                    <input data-testid="input-loved-one" value={form.loved_one_name} onChange={update("loved_one_name")} placeholder="Robert A. Thompson" required />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="field-label">Service date (if any)</label>
                    <input data-testid="input-service-date" type="date" value={form.service_date} onChange={update("service_date")} />
                  </div>
                  <div>
                    <label className="field-label">Photo count estimate</label>
                    <select data-testid="input-photo-count" value={form.photo_count} onChange={update("photo_count")}>
                      <option value="">Select a range</option>
                      {PHOTO_COUNTS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="field-label">Style preference</label>
                    <select data-testid="input-style" value={form.style} onChange={update("style")}>
                      <option value="">Not sure yet</option>
                      {STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Music preference</label>
                    <select data-testid="input-music" value={form.music_preference} onChange={update("music_preference")}>
                      <option value="">Select a preference</option>
                      {MUSIC_PREFS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="field-label">Message</label>
                  <textarea
                    data-testid="input-message"
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Share a song title, a line about their life, or anything we should know."
                  />
                </div>

                <div className="flex items-center justify-between gap-4 flex-wrap pt-3">
                  <p className="text-xs text-[#A0988E] max-w-sm">
                    Next, you&apos;ll get your private photo upload link instantly.
                  </p>
                  <button
                    type="submit"
                    data-testid="submit-tribute-request"
                    disabled={loading}
                    className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Submit Request <Send size={14} /></>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
